import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import { getApiEndpoints } from '@/services/api/endpoints';
import { isNotFoundError } from '@/lib';
import type {
  ConfirmPhoneVerificationResponse,
  ListUsersResponse,
  OrgMember,
  StartPhoneVerificationResponse,
} from './usersTypes';
import type {
  BusinessProfileBootstrapInput,
  BusinessProfileBootstrapRequest,
  BusinessProfileUpdateRequest,
  IBusinessUser,
} from '@/domain/entities/user';
import type { CreateItemResponse } from '@/services/api/types';
import { parseApplicationUserId } from '@/domain/identity';

function normalizeBusinessUser(profile: IBusinessUser): IBusinessUser {
  return {
    ...profile,
    applicationUserId: parseApplicationUserId(profile.applicationUserId),
  };
}

function toBusinessProfileUpdateRequest(profile: IBusinessUser): BusinessProfileUpdateRequest {
  return {
    phone: profile.phone,
    firstName: profile.firstName,
    lastName: profile.lastName,
    email: profile.email,
    address: profile.address,
  };
}

function toBusinessProfileBootstrapRequest(
  email: string,
  input?: BusinessProfileBootstrapInput,
): BusinessProfileBootstrapRequest {
  return {
    profile: {
      email,
      firstName: input?.firstName,
      lastName: input?.lastName,
      phone: input?.phone,
      address: input?.address,
    },
  };
}

// Self-profile reads are resolved by the API from the authenticated Cognito identity.
export const fetchUserProfile = createAppAsyncThunk<IBusinessUser, void>(
  'users/fetchProfile',
  async (_, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = getApiEndpoints().core.user.getUser;
      logger?.debug('[fetchUserProfile] Fetching self profile', { endpoint });

      const response = await apiService.get<IBusinessUser>(endpoint);
      logger?.debug('[fetchUserProfile] Self profile loaded');
      return normalizeBusinessUser(response);
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };

      if (isNotFoundError(error)) {
        logger?.info('[fetchUserProfile] Profile not found, bootstrap required', { status: error.status });
        return rejectWithValue({ message: 'User not found', needsOnboarding: true });
      }

      logger?.error('[fetchUserProfile] Failed to load profile', {
        status: error.status,
        message: error.message,
        body: error.body,
      });
      return rejectWithValue({ message: error.message });
    }
  },
);

export const fetchUsers = createAppAsyncThunk<ListUsersResponse, { limit?: number; nextToken?: string } | void>(
  'users/fetchUsers',
  async (arg, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    const limit = arg && 'limit' in arg ? arg.limit : undefined;
    const nextToken = arg && 'nextToken' in arg ? arg.nextToken : undefined;

    try {
      const base = getApiEndpoints().core.user.getUser;

      const qs = new URLSearchParams({ list: 'true' });
      if (limit) qs.set('limit', String(limit));
      if (nextToken) qs.set('nextToken', nextToken);

      const endpoint = qs.toString() ? `${base}?${qs.toString()}` : base;

      logger?.info('[fetchUsers] Fetching from:', endpoint);

      const response = await apiService.get<ListUsersResponse>(endpoint);
      logger?.info('[fetchUsers] API response:', response);

      return { ...response, items: response.items.map(normalizeBusinessUser) };
    } catch (err) {
      const error = err as Error;
      logger?.error('[fetchUsers] Error:', error.message);
      return rejectWithValue({ message: error.message });
    }
  },
);

// Idempotent application-profile bootstrap. The API owns all identity and authorization fields.
export const bootstrapUserProfile = createAppAsyncThunk<IBusinessUser, BusinessProfileBootstrapInput | void>(
  'users/bootstrapUserProfile',
  async (input, { extra: { config }, getState, rejectWithValue }) => {
    const { apiService, logger } = config;
    const email = getState().auth.email;

    try {
      logger?.info('[bootstrapUserProfile] Starting idempotent profile bootstrap');

      if (!email) {
        logger?.error('[bootstrapUserProfile] No email found in authenticated session');
        return rejectWithValue({ message: 'Email is required to create profile' });
      }

      const endpoint = getApiEndpoints().core.user.createUser;
      const request = toBusinessProfileBootstrapRequest(email, input || undefined);
      const response = await apiService.post<CreateItemResponse<IBusinessUser>, BusinessProfileBootstrapRequest>(
        endpoint,
        request,
      );
      return normalizeBusinessUser(response.data);
    } catch (err) {
      const error = err as Error;
      logger?.error('[bootstrapUserProfile] Failed to bootstrap profile', { message: error.message });
      return rejectWithValue({ message: error.message });
    }
  },
);

// Update user profile
export const updateUserProfile = createAppAsyncThunk<CreateItemResponse<IBusinessUser>, { profile: IBusinessUser }>(
  'users/updateProfile',
  async ({ profile }, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = getApiEndpoints().core.user.updateUser;
      logger?.info('[updateUserProfile] Updating authenticated user profile');

      const response = await apiService.put<CreateItemResponse<IBusinessUser>, BusinessProfileUpdateRequest>(
        endpoint,
        toBusinessProfileUpdateRequest(profile),
      );
      return { ...response, data: normalizeBusinessUser(response.data) };
    } catch (err) {
      const error = err as Error;
      logger?.error('[updateUserProfile] Error:', error.message);
      return rejectWithValue({ message: error.message });
    }
  },
);

export const updateBusinessUserOrgCreation = createAppAsyncThunk<
  { applicationUserId: string; allowCreateOrg: boolean },
  { applicationUserId: string; allowCreateOrg: boolean }
>(
  'users/updateBusinessUserOrgCreation',
  async ({ applicationUserId, allowCreateOrg }, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;
    try {
      const endpoint = `${getApiEndpoints().core.user.adminBusinessProfile}/${encodeURIComponent(applicationUserId)}`;
      const response = await apiService.patch<{
        data: { applicationUserId: string; allowCreateOrg: boolean };
      }>(endpoint, { changes: { allowCreateOrg } });
      if (response.data?.applicationUserId !== applicationUserId || response.data.allowCreateOrg !== allowCreateOrg) {
        throw new Error('The updated organization creation permission could not be verified.');
      }
      return response.data;
    } catch (err) {
      const error = err as Error;
      logger?.error('[updateBusinessUserOrgCreation] Failed to update organization creation permission', {
        applicationUserId,
        message: error.message,
      });
      return rejectWithValue({ message: error.message || 'Failed to update organization creation permission' });
    }
  },
);

// ========== PHONE VERIFICATION THUNKS ==========

export const startPhoneVerification = createAppAsyncThunk<StartPhoneVerificationResponse, { phoneE164: string }>(
  'users/startPhoneVerification',
  async ({ phoneE164 }, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = getApiEndpoints().core.phoneVerification.sendVerification;

      logger?.info('[startPhoneVerification] Sending SMS verification', {
        phoneE164: phoneE164 ? `${phoneE164.slice(0, 2)}******${phoneE164.slice(-4)}` : undefined,
      });

      const response = await apiService.post<StartPhoneVerificationResponse>(endpoint, {
        phone: phoneE164,
      });

      logger?.info('[startPhoneVerification] Response:', response);
      return response;
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      logger?.error('[startPhoneVerification] Failed to start phone verification', {
        message: error.message,
        status: error.status,
        body: error.body,
      });
      return rejectWithValue({ message: error.message });
    }
  },
);

export const confirmPhoneVerification = createAppAsyncThunk<
  ConfirmPhoneVerificationResponse,
  { phoneE164: string; code: string }
>('users/confirmPhoneVerification', async ({ phoneE164, code }, { extra: { config }, rejectWithValue }) => {
  const { apiService, logger } = config;

  try {
    const endpoint = getApiEndpoints().core.phoneVerification.verifyCode;

    logger?.info('[confirmPhoneVerification] Confirming SMS code', {
      phoneE164: phoneE164 ? `${phoneE164.slice(0, 2)}******${phoneE164.slice(-4)}` : undefined,
    });

    // body should match your backend confirm handler
    const response = await apiService.post<ConfirmPhoneVerificationResponse>(endpoint, {
      phone: phoneE164,
      code,
    });

    logger?.info('[confirmPhoneVerification] Response:', response);
    return response;
  } catch (err) {
    const error = err as Error & { status?: number; body?: string };
    logger?.error('[confirmPhoneVerification] Failed to confirm phone verification', {
      message: error.message,
      status: error.status,
      body: error.body,
    });
    return rejectWithValue({ message: error.message });
  }
});

// ========== MEMBER THUNKS ==========

// Fetch all members for an organization
export const fetchOrgMembers = createAppAsyncThunk<OrgMember[], { orgId: string }>(
  'users/fetchOrgMembers',
  async ({ orgId }, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = `${getApiEndpoints().core.members.getMembers}?orgId=${orgId}`;
      logger?.info('[fetchOrgMembers] Fetching organization members', { endpoint, orgId });

      const response = await apiService.get<OrgMember[]>(endpoint);
      logger?.debug('[fetchOrgMembers] Response received', { orgId, members: response.length });

      return response;
    } catch (err) {
      const error = err as Error;
      logger?.error('[fetchOrgMembers] Failed to fetch organization members', { orgId, message: error.message });
      return rejectWithValue({ message: error.message });
    }
  },
);

// Update a member's role
export const updateMemberRole = createAppAsyncThunk<OrgMember, { memberId: string; roleId: string }>(
  'users/updateMemberRole',
  async ({ memberId, roleId }, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = getApiEndpoints().core.members.updateMember;
      logger?.info('[updateMemberRole] Updating member role', { memberId, roleId });

      const response = await apiService.put<OrgMember>(endpoint, { memberId, roleId });
      logger?.debug('[updateMemberRole] Response received', { memberId, roleId });

      return response;
    } catch (err) {
      const error = err as Error;
      logger?.error('[updateMemberRole] Failed to update member role', { memberId, roleId, message: error.message });
      return rejectWithValue({ message: error.message });
    }
  },
);

// Remove a member from the organization - returns the memberId to remove from state
export const removeMember = createAppAsyncThunk<string, { memberId: string }>(
  'users/removeMember',
  async ({ memberId }, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = `${getApiEndpoints().core.members.removeMember}?id=${memberId}`;
      logger?.info('[removeMember] Removing member', { memberId });

      await apiService.delete(endpoint);
      logger?.debug('[removeMember] Member removed', { memberId });

      return memberId;
    } catch (err) {
      const error = err as Error;
      logger?.error('[removeMember] Failed to remove member', { memberId, message: error.message });
      return rejectWithValue({ message: error.message });
    }
  },
);
